import { useEffect, useRef, useState, useCallback, Fragment } from 'react';
import { api } from '../lib/api';
import { GenreRow } from '../components/GenreRow';
import { Seo } from '../components/Seo';
import { AdSlot } from '../components/AdSlot';
import { AdSenseUnit } from '../components/AdSenseUnit';
import { useAuth } from '../lib/auth';
import { isSafeMode } from '../lib/safeMode';
import { useT } from '../i18n';
import { Link } from 'react-router-dom';
import { Flame, Film, Sparkles, Upload, Volume2, VolumeX, RefreshCw } from 'lucide-react';
import { fileUrl } from '../lib/api';

export default function HomePage() {
    const { user } = useAuth();
    const t = useT();
    const [sections, setSections] = useState([]);
    const [configuredVideo, setConfiguredVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const heroVideoRef = useRef(null);
    const [heroMuted, setHeroMuted] = useState(() => localStorage.getItem('alb_video_muted') !== 'false');

    const loadHome = useCallback(() => {
        const state = { cancelled: false, timer: null };
        const attemptLoad = async (attempt) => {
            try {
                const r = await api.get('/videos/home');
                if (state.cancelled) return;
                let secs = r.data.sections || [];
                const featured = r.data.featured_video || null;
                if (isSafeMode()) {
                    // Defensive filter: never show +18 items in safe mode.
                    secs = secs
                        .map((s) => ({ ...s, items: (s.items || []).filter((v) => !v.is_adult) }))
                        .filter((s) => s.key === 'live' || (s.items || []).length > 0);
                }
                setSections(secs);
                setConfiguredVideo(featured?.is_adult && isSafeMode() ? null : featured);
                setLoadFailed(false);
                setLoading(false);
            } catch {
                if (state.cancelled) return;
                // Render's free tier can take a while to wake up from a cold start;
                // retry a few times with backoff before showing a manual retry option.
                if (attempt < 3) {
                    state.timer = setTimeout(() => attemptLoad(attempt + 1), 3000 * (attempt + 1));
                } else {
                    setLoadFailed(true);
                    setLoading(false);
                }
            }
        };
        attemptLoad(0);
        return () => { state.cancelled = true; if (state.timer) clearTimeout(state.timer); };
    }, []);

    useEffect(() => {
        const cancel = loadHome();
        return cancel;
    }, [user?.user_id, loadHome]);

    const retryLoadHome = () => {
        setLoading(true);
        setLoadFailed(false);
        loadHome();
    };

    const trending = sections.find((s) => s.key === 'trending');
    const shorts = sections.find((s) => s.key === 'shorts');
    const others = sections.filter((s) => !['trending', 'shorts'].includes(s.key));
    const featuredVideo = configuredVideo || sections
        .flatMap((section) => section.items || [])
        .find((video) => video.video_url);
    const heroVideoSrc = featuredVideo ? fileUrl(featuredVideo.video_url) : '/videos/welcome.mp4';

    useEffect(() => {
        if (heroVideoRef.current) heroVideoRef.current.muted = heroMuted;
    }, [heroMuted, heroVideoSrc]);

    // Localize known section titles
    const localizeTitle = (key, fallback) => {
        if (key === 'trending') return t('section.trending');
        if (key === 'shorts') return t('section.shorts');
        if (key === 'for_you') return t('section.for_you');
        return fallback;
    };

    return (
        <main className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-5 md:py-8 space-y-8 md:space-y-12 pb-24 md:pb-12" data-testid="home-page">
            <Seo path="/" title="Vídeos i Shorts en català" description="Albambam és la plataforma de vídeo en català: mira vídeos i shorts, descobreix creadors per categories i guanya amb el teu contingut." />
            {/* Hero */}
            <section className="relative min-h-[430px] md:min-h-[520px] overflow-hidden rounded-[20px] border border-[color:var(--alb-hairline)] bg-[color:var(--alb-surface)] flex items-end">
                <video
                    ref={heroVideoRef}
                    key={heroVideoSrc}
                    src={heroVideoSrc}
                    autoPlay
                    muted={heroMuted}
                    loop
                    playsInline
                    preload="auto"
                    className="absolute inset-0 h-full w-full object-cover"
                    data-testid="home-hero-video"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-black/10" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
                <button
                    type="button"
                    onClick={() => {
                        const nextMuted = !heroMuted;
                        if (heroVideoRef.current) {
                            heroVideoRef.current.muted = nextMuted;
                            if (!nextMuted) heroVideoRef.current.play().catch(() => {});
                        }
                        setHeroMuted(nextMuted);
                        localStorage.setItem('alb_video_muted', String(nextMuted));
                    }}
                    className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur-sm hover:bg-black/65"
                    aria-label={heroMuted ? 'Activar el so' : 'Silenciar el vídeo'}
                    title={heroMuted ? 'Activar el so' : 'Silenciar el vídeo'}
                    data-testid="hero-sound-toggle"
                >
                    {heroMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <div className="relative z-10 max-w-2xl p-6 md:p-10">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-1 bg-[rgba(244,200,74,0.18)] text-[color:var(--alb-sun)]"><Sparkles className="h-3 w-3" /> {t('home.hero.badge')}</span>
                    <h1 className="font-display text-3xl md:text-5xl font-bold mt-3 leading-tight">{t('home.hero.title_1')} <span className="alb-text-gradient">{t('home.hero.title_2')}</span></h1>
                    <p className="text-white/75 mt-3 md:text-lg">{t('home.hero.subtitle')}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {user && (
                            <Link to="/pujar" data-testid="hero-upload-cta" className="inline-flex items-center gap-2 rounded-xl bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] px-4 py-2 font-semibold hover:bg-[color:var(--alb-tangerine)]">
                                <Upload className="h-4 w-4" /> {t('nav.upload')}
                            </Link>
                        )}
                        <Link to="/shorts" data-testid="hero-shorts-cta" className="inline-flex items-center gap-2 rounded-xl bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] px-4 py-2 font-semibold hover:bg-[color:var(--alb-tangerine)]"><Film className="h-4 w-4" /> {t('home.hero.cta_shorts')}</Link>
                        <Link to="/categories" data-testid="hero-cats-cta" className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--alb-hairline)] px-4 py-2 font-semibold hover:bg-[color:var(--alb-surface)]"><Flame className="h-4 w-4" /> {t('home.hero.cta_trending')}</Link>
                    </div>
                </div>
            </section>

            <AdSlot placement="banner" />

            {loadFailed && sections.length === 0 && (
                <div className="flex flex-col items-center gap-3 rounded-[16px] border border-[color:var(--alb-hairline)] bg-[color:var(--alb-surface)] p-8 text-center" data-testid="home-load-error">
                    <p className="text-[color:var(--alb-text-muted)]">No s\u2019han pogut carregar els v\u00eddeos. Comprova la connexi\u00f3 i torna-ho a provar.</p>
                    <button
                        type="button"
                        onClick={retryLoadHome}
                        className="inline-flex items-center gap-2 rounded-xl bg-[color:var(--alb-sun)] px-4 py-2 font-semibold text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]"
                        data-testid="home-retry-button"
                    >
                        <RefreshCw className="h-4 w-4" /> Torna-ho a provar
                    </button>
                </div>
            )}

            {trending && <GenreRow title={t('section.trending')} items={trending.items} sectionTestId="row-trending" />}
            {shorts && (
                <section data-testid="row-shorts-teaser">
                    <div className="flex items-end justify-between mb-3 px-1">
                        <h2 className="font-display text-xl md:text-2xl font-semibold">{t('section.shorts')} <span className="text-[color:var(--alb-pink)]">•</span></h2>
                        <Link to="/shorts" className="text-sm text-[color:var(--alb-sun)] hover:underline">{t('home.shorts.see_all')}</Link>
                    </div>
                    <div className="no-scrollbar flex gap-3 overflow-x-auto snap-x pb-2">
                        {shorts.items.map((v) => (
                            <Link key={v.video_id} to={`/shorts?id=${v.video_id}`} className="snap-start shrink-0 w-[150px] sm:w-[180px] rounded-[14px] overflow-hidden border border-[color:var(--alb-hairline)] bg-[color:var(--alb-surface)] group relative" data-testid="shorts-teaser-card">
                                <div className="aspect-[9/16] w-full relative">
                                    {v.thumbnail_url ? (
                                        <img src={fileUrl(v.thumbnail_url)} alt={v.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                                    ) : (
                                        <video src={fileUrl(v.video_url)} className="absolute inset-0 h-full w-full object-cover" muted preload="metadata" />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
                                    <div className="absolute bottom-2 left-2 right-2 text-white">
                                        <div className="text-[12px] font-semibold line-clamp-2">{v.title}</div>
                                        <div className="text-[10px] text-white/70">{v.uploader_name}</div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            <AdSenseUnit placement="home" />

            {others.map((s, index) => (
                <Fragment key={s.key}>
                    <GenreRow title={localizeTitle(s.key, s.title)} items={s.items} sectionTestId={`row-${s.key}`} />
                    {index % 3 === 2 && <AdSenseUnit placement="home" />}
                </Fragment>
            ))}

            {(!loading && sections.length === 0) && (
                <div data-testid="home-empty" className="alb-card p-8 text-center">
                    <h2 className="font-display text-xl mb-2">{t('home.empty.title')}</h2>
                    <p className="text-[color:var(--alb-text-muted)]">{t('home.empty.subtitle')}</p>
                </div>
            )}

            {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-video rounded-[14px] alb-pulse" />)}
                </div>
            )}
        </main>
    );
}
